import { useRef, useState } from "react"
import { ReviewPlanWrapper, ReviewPlanHeader, ReviewPlanContent } from "./styled"
import {
  HeaderBackBox,
  HeaderBackButton,
  HeaderInfoBox,
  HeaderInfoButton,
  MeshDisclaimerPopover,
} from "../../styled"
import { useAppSelector } from "app/hooks"
import { getDisplayWeightWithUnit } from "utils/weightConverter"
import { useBodyDisplayUnits } from "features/profile/hooks/body"
import MeshViewer from "components/MeshViewer"
import YourDailyTarget from "components/onboarding/Goal/YourDailyTarget"
import WeightTransition from "components/onboarding/Goal/WeightTransition"
import ChevronLeftBold from "components/Icons/ChevronLefBold"
import InfoOutlineIcon from "components/Icons/InfoOutlineIcon"
import CloseWhiteIcon from "components/Icons/CloseWhiteIcon"
import FixCtaBox from "components/Common/FixCtaBox"
import Button from "components/Buttons/Button"

interface ReviewPlanProps {
  onBack: () => void
  onGetStarted: () => void
}

const ReviewPlan = ({ onBack, onGetStarted }: ReviewPlanProps) => {
  const goal = useAppSelector((state) => state.onboarding.goal)
  const { bodyDisplayUnits } = useBodyDisplayUnits()
  const [isFullscreen, setIsFullScreen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  const displayedStartWeight = getDisplayWeightWithUnit(
    bodyDisplayUnits,
    goal?.weight,
  )

  const displayedTargetWeight = getDisplayWeightWithUnit(
    bodyDisplayUnits,
    goal?.targetWeight ?? 0,
  )

  if (!goal) {
    return null
  }

  return (
    <ReviewPlanWrapper
      ref={containerRef}
      style={{
        overflow: isFullscreen ? "hidden" : "auto",
      }}
    >
      <ReviewPlanHeader>
        <HeaderBackBox>
          {isFullscreen ? (
            <HeaderBackButton onClick={() => setIsFullScreen(false)}>
              <CloseWhiteIcon />
            </HeaderBackButton>
          ) : (
            <HeaderBackButton onClick={onBack}>
              <ChevronLeftBold fill="#4BD9DE" />
            </HeaderBackButton>
          )}
        </HeaderBackBox>
        {goal.modelMesh && !isFullscreen && (
          <HeaderInfoBox>
            <MeshDisclaimerPopover
              content="These meshes provide an estimations to help you visualize your transformation and are for reference purposes only."
              placement="bottom-end"
              trigger="click"
            >
              <HeaderInfoButton>
                <InfoOutlineIcon />
              </HeaderInfoButton>
            </MeshDisclaimerPopover>
          </HeaderInfoBox>
        )}
      </ReviewPlanHeader>

      {goal.modelMesh ? (
        <MeshViewer
          measurementMesh={goal.modelMesh.measurementMesh}
          targetMesh={goal.modelMesh.targetMesh}
          isFullScreen={isFullscreen}
          setIsFullScreen={setIsFullScreen}
          displayedStartWeight={displayedStartWeight}
          displayedTargetWeight={displayedTargetWeight}
          goal={goal}
          zoomDistance={2.75}
        />
      ) : (
        <WeightTransition
          goal={goal}
          displayedStartWeight={displayedStartWeight}
          displayedTargetWeight={displayedTargetWeight}
          style={{ marginTop: 6 }}
        />
      )}

      <ReviewPlanContent
        style={{
          transform: isFullscreen ? "translateY(30px)" : "",
        }}
      >
        <YourDailyTarget goal={goal} showCustomize={false} />
      </ReviewPlanContent>

      <FixCtaBox
        style={{
          opacity: isFullscreen ? 0 : 1,
          transition: `all ${isFullscreen ? 0.1 : 0.2}s linear`,
        }}
      >
        <Button
          $theme="dark"
          $borderLess
          style={{ width: "100%" }}
          $type="primary"
          onClick={onGetStarted}
        >
          Get Started
        </Button>
      </FixCtaBox>
    </ReviewPlanWrapper>
  )
}

export default ReviewPlan
